/**
 * Everything the board DERIVES from one payload: the tree, the column template,
 * and the figures on the strip.
 *
 * Extracted from HiringDemandTab at the 500-line ceiling, and the seam is a real
 * one — that file is about the state a lead changes, and every line here is a
 * pure function of the payload plus one toggle. Nothing in it writes.
 */
import { useMemo } from 'react';
import type { DemandBoard } from '../../../types/taDemand';
import {
  backfillsByEmployee,
  buildTracks,
  buildTree,
  heads,
  rolesOfDept,
  summarise,
  type Track,
} from './demandModel';
import { END_W, GROUP_W, MONTH_W, NAME_W } from './DemandChrome';
import { unitChoices } from './Restructure';
import { applyWindow, type DemandWindow } from './demandWindow';

export function useDemandLayout(
  board: DemandBoard | null,
  showPast: boolean,
  win: DemandWindow | null,
) {
  /*
    THE WINDOW NARROWS THE BOARD, not the timeline. Which roles get a row is the
    question it answers — a seat filled in April is this year's hire on a year
    board and a colleague who was already here on a T3 board. The month columns
    stay the full year either way, so a narrowed board still shows where its
    roles sit relative to the rest of the year.
  */
  const scoped = useMemo(() => (board && win ? applyWindow(board, win) : board), [board, win]);
  const tracks = useMemo<Track[]>(
    () => (board ? buildTracks(board.months, showPast) : []),
    [board, showPast],
  );
  const tree = useMemo(() => (scoped ? buildTree(scoped) : []), [scoped]);
  // Every unit a row could be moved into, built from the tree so a unit that
  // only exists because somebody moved something there is still a destination.
  const units = useMemo(() => unitChoices(tree), [tree]);
  const backfills = useMemo(
    () => (scoped ? backfillsByEmployee(scoped) : new Map<string, string>()),
    [scoped],
  );
  /*
    THE KPIs IGNORE THE WINDOW, and must. "Hired YTD" and "Hired this trimester"
    are counted on the month the OFFER was accepted — a TA delivery statistic —
    while the window selects rows by the month somebody STARTS. Summing the
    narrowed board would drop hires whose start falls outside the span and report
    a year's delivery as smaller than it was. So the strip describes the whole
    board; the window only decides which rows are drawn.
  */
  const fullTree = useMemo(() => (board ? buildTree(board) : []), [board]);
  const stats = useMemo(() => (board ? summarise(board, fullTree) : null), [board, fullTree]);

  const template = useMemo(
    () =>
      // END_W is zero now — the group roll-up lives in the group row's own frozen
      // cell. Kept in the arithmetic rather than deleted so that bringing a
      // trailing column back is a number, not a re-derivation.
      `${NAME_W}px ${tracks
        .map((t) => (t.kind === 'group' ? `${GROUP_W}px` : `${MONTH_W}px`))
        .join(' ')}${END_W ? ` ${END_W}px` : ''}`,
    [tracks],
  );
  const minWidth = useMemo(
    () =>
      NAME_W + END_W + tracks.reduce((s, t) => s + (t.kind === 'group' ? GROUP_W : MONTH_W), 0),
    [tracks],
  );
  const pastHires = useMemo(() => {
    if (!board) return 0;
    const pastKeys = new Set(board.months.filter((m) => m.past).map((m) => m.key));
    return heads(tree.flatMap(rolesOfDept).filter((r) => r.month !== null && pastKeys.has(r.month)));
  }, [board, tree]);

  return { tracks, tree, units, backfills, stats, template, minWidth, pastHires };
}
